import type { FollowupRule, FollowupSettingsValue } from "@/api/types";

/**
 * Follow-up rules, as the backend checks them (src/modules/followups/followup-rules.ts): the
 * built-in defaults (the demo and "new rule" start from them) and the same validation, so the
 * editor explains a problem before saving.
 */
export const FOLLOWUP_STAGES = ["NEW", "IDENTIFYING", "ENRICHING", "VALUED", "QUALIFYING", "ENGAGED", "OFFER_SENT", "NEGOTIATING"] as const;

export const DEFAULT_FOLLOWUP_SETTINGS: FollowupSettingsValue = {
  enabled: true,
  sellerRequested: { enabled: true, defaultTime: "10:00", defaultDays: 3, maxDaysAhead: 120 },
  priceGap: { action: "FOLLOW_UP", everyDays: 10 },
  rules: [
    {
      id: "price-not-given",
      name: "No price given in a day: make the offer",
      enabled: true,
      when: { stages: [], offer: "NONE", maxOffer: "SET", silentDays: 1 },
      action: "OFFER_AGAIN",
      maxAttempts: 1,
      repeatEveryDays: 3,
      instructions: null,
      markUnresponsiveAfterDays: null,
    },
    {
      id: "quiet-before-offer",
      name: "Quiet before an offer",
      enabled: true,
      when: { stages: [], offer: "NONE", maxOffer: "ANY", silentDays: 2 },
      action: "CHECK_IN",
      maxAttempts: 2,
      repeatEveryDays: 3,
      instructions: null,
      markUnresponsiveAfterDays: null,
    },
    {
      id: "quiet-after-offer",
      name: "Quiet after an offer",
      enabled: true,
      when: { stages: [], offer: "MADE", maxOffer: "ANY", silentDays: 2 },
      action: "OFFER_AGAIN",
      maxAttempts: 2,
      repeatEveryDays: 3,
      instructions: null,
      markUnresponsiveAfterDays: null,
    },
    {
      id: "refresh-offer",
      name: "Still quiet: refresh the API offer",
      enabled: true,
      when: { stages: [], offer: "MADE", maxOffer: "ANY", silentDays: 7 },
      action: "RERUN_API_OFFER",
      maxAttempts: 1,
      repeatEveryDays: 3,
      instructions: null,
      markUnresponsiveAfterDays: 7,
    },
  ],
};

export function newRule(existing: FollowupRule[]): FollowupRule {
  let n = existing.length + 1;
  while (existing.some((r) => r.id === `rule-${n}`)) n++;
  return {
    id: `rule-${n}`,
    name: "New rule",
    enabled: true,
    when: { stages: [], offer: "ANY", maxOffer: "ANY", silentDays: 3 },
    action: "CHECK_IN",
    maxAttempts: 1,
    repeatEveryDays: 3,
    instructions: null,
    markUnresponsiveAfterDays: null,
  };
}

const int = (v: number, min: number, max: number) => Number.isInteger(v) && v >= min && v <= max;

/** Field errors keyed like the backend's ("rules.0.name"); empty when valid. */
export function followupSettingsErrors(s: FollowupSettingsValue): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s.sellerRequested.defaultTime)) errors["sellerRequested.defaultTime"] = "Use HH:MM (24h)";
  if (!int(s.sellerRequested.defaultDays, 1, 30)) errors["sellerRequested.defaultDays"] = "1 to 30 days";
  if (!int(s.sellerRequested.maxDaysAhead, 1, 365)) errors["sellerRequested.maxDaysAhead"] = "1 to 365 days";
  if (!int(s.priceGap.everyDays, 1, 90)) errors["priceGap.everyDays"] = "1 to 90 days";
  if (s.rules.length > 20) errors.rules = "At most 20 rules";
  const seen = new Set<string>();
  s.rules.forEach((r, i) => {
    const at = (f: string) => `rules.${i}.${f}`;
    if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(r.id)) errors[at("id")] = "Use lowercase letters, digits and dashes";
    else if (seen.has(r.id)) errors[at("id")] = "Rule ids must be unique";
    seen.add(r.id);
    if (!r.name.trim()) errors[at("name")] = "Name the rule";
    else if (r.name.length > 80) errors[at("name")] = "Keep it under 80 characters";
    if (!int(r.when.silentDays, 1, 90)) errors[at("when.silentDays")] = "1 to 90 days";
    if (!int(r.maxAttempts, 1, 10)) errors[at("maxAttempts")] = "1 to 10";
    if (!int(r.repeatEveryDays, 1, 90)) errors[at("repeatEveryDays")] = "1 to 90 days";
    if ((r.instructions ?? "").length > 500) errors[at("instructions")] = "Keep it under 500 characters";
    if (r.markUnresponsiveAfterDays !== null && !int(r.markUnresponsiveAfterDays, 1, 90)) errors[at("markUnresponsiveAfterDays")] = "1 to 90 days";
  });
  return errors;
}
